import { JoinContinuePage } from './pages/JoinContinuePage';
import { KelilingoDownloadPage } from './pages/KelilingoDownloadPage';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppLayout } from './components/AppLayout';
import { DemographicsPromptHost } from './components/DemographicsPromptHost';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { FactoryWorkspaceProvider } from './context/FactoryWorkspaceContext';
import {
  FactoryShell,
  FactoryOrdersPage,
  FactoryShelfPage,
  FactoryTasksPage,
  FactoryTaskDetailPage,
  FactoryTaskResultsPage,
  FactoryGoodsPage,
  FactoryReleasePage,
  FactorySettingsDomainPage,
} from './factory';
import { OwnerFactoryAuthPage } from './pages/OwnerFactoryAuthPage';
import { LearningFactoryOrdersPage } from './pages/LearningFactoryOrdersPage';
import { LearningFactoryOrderDetailPage } from './pages/LearningFactoryOrderDetailPage';
import { LearningFactoryOrderParticlePage } from './pages/LearningFactoryOrderParticlePage';
import { FamilyPlanPage } from './pages/FamilyPlanPage';
import { FactoryStaffAdminPage } from './pages/FactoryStaffAdminPage';
import { AboutPage, CopyrightPage, PrivacyPage, SupportPage, TermsPage } from './pages/LegalPages';
import { DownloadPage } from './pages/DownloadPage';
import {
  AnnotationsPage,
  BookmarksPage,
  FeedbackPage,
  LearningFootprintPage,
  LearningSequencePage,
  LearningSettingsPage,
  ProfileModelsPage,
  ScanUploadPage,
} from './pages/LearningPages';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { AssetUploadPage } from './pages/AssetUploadPage';
import { AssetsPage } from './pages/AssetsPage';
import { PasLibraryPage } from './pages/PasLibraryPage';
import { HomePage } from './pages/HomePage';
import { KnowledgeDetailPage } from './pages/KnowledgeDetailPage';
import { KnowledgePasListPage } from './pages/KnowledgePasListPage';
import { KnowledgePage } from './pages/KnowledgePage';
import { LearningRecordPage } from './pages/LearningRecordPage';
import { LoginPage } from './pages/LoginPage';
import { WechatOAuthBindPage } from './pages/WechatOAuthBindPage';
import { WechatOAuthCallbackPage } from './pages/WechatOAuthCallbackPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { DeleteAccountPage } from './pages/DeleteAccountPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { ChangePasswordPage, ProfilePage } from './pages/ProfilePage';
import { LearningAiChatsPage } from './pages/LearningAiChatsPage';
import { LearningAiChatDetailPage } from './pages/LearningAiChatDetailPage';
import { LearningGoalsPage } from './pages/LearningGoalsPage';
import { SubjectModulesPage } from './pages/SubjectModulesPage';
import { LearningPositionPage } from './pages/LearningPositionPage';
import { SpineReviewCoveragePage } from './pages/SpineReviewCoveragePage';
import { ReviewHomePage } from './pages/ReviewHomePage';
import { ReviewSessionPage } from './pages/ReviewSessionPage';
import { TtqaListPage } from './pages/TtqaListPage';
import { TtqMembershipPressurePage } from './pages/TtqMembershipPressurePage';
import { TtqMembershipPressureDetailPage } from './pages/TtqMembershipPressureDetailPage';
import { TtqSessionPage } from './pages/TtqSessionPage';
import { TtqIpttqDetailPage } from './pages/TtqIpttqDetailPage';
import { TtqMultiSolutionWorkbenchPage } from './pages/ttq/TtqMultiSolutionWorkbenchPage';
import { TtqQuestionChainWorkbenchPage } from './pages/ttq/TtqQuestionChainWorkbenchPage';
import { TtqPatternUnifyWorkbenchPage } from './pages/ttq/TtqPatternUnifyWorkbenchPage';
import { TtqQuestionsChainWorkbenchPage } from './pages/ttq/TtqQuestionsChainWorkbenchPage';
import { TtqCttqWorkbenchPage } from './pages/ttq/TtqCttqWorkbenchPage';
import { TtqSessionAggregateAWorkbenchPage } from './pages/ttq/TtqSessionAggregateAWorkbenchPage';
import { TtqSessionAggregateBWorkbenchPage } from './pages/ttq/TtqSessionAggregateBWorkbenchPage';
import { TTQ_USER_ENTRY_ENABLED } from './services/ttqService';

export default function App() {
  return (
    <AuthProvider>
      <DemographicsPromptHost>
        <BrowserRouter>
        <FactoryWorkspaceProvider>
        <Routes>
          <Route path="/welcome" element={<OnboardingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/join/continue" element={<JoinContinuePage />} />
          <Route path="/login/oauth/wechat" element={<WechatOAuthCallbackPage />} />
          <Route path="/login/oauth/wechat/bind" element={<WechatOAuthBindPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/copyright" element={<CopyrightPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/kelilingo-download" element={<KelilingoDownloadPage />} />

          <Route element={<ProtectedRoute />}>
            <Route path="factory" element={<FactoryShell />}>
              <Route index element={<Navigate to="orders" replace />} />
              <Route path="orders" element={<FactoryOrdersPage />} />
              <Route path="shelf" element={<FactoryShelfPage />} />
              <Route path="tasks" element={<FactoryTasksPage />} />
              <Route path="tasks/:batchId" element={<FactoryTaskDetailPage />} />
              <Route path="tasks/:batchId/results" element={<FactoryTaskResultsPage />} />
              <Route path="goods" element={<FactoryGoodsPage />} />
              <Route path="release" element={<FactoryReleasePage />} />
              <Route path="settings" element={<FactorySettingsDomainPage />} />
              <Route path="staff" element={<FactoryStaffAdminPage />} />
              <Route
                path="batches/:batchId"
                element={<Navigate to="/factory/tasks" replace />}
              />
            </Route>

            <Route element={<AppLayout />}>
              <Route index element={<HomePage />} />
              <Route path="assets" element={<AssetsPage />} />
              <Route path="pas" element={<PasLibraryPage />} />
              <Route path="assets/upload" element={<AssetUploadPage />} />
              <Route path="assets/:assetId" element={<AssetDetailPage />} />
              <Route path="assets/:assetId/:workspaceView" element={<AssetDetailPage />} />
              <Route path="factory-orders" element={<LearningFactoryOrdersPage />} />
              <Route path="factory-orders/:orderId" element={<LearningFactoryOrderDetailPage />} />
              <Route path="factory-orders/:orderId/particles/:draftId" element={<LearningFactoryOrderParticlePage />} />
              <Route path="family-plan" element={<FamilyPlanPage />} />
              <Route path="owner/batches" element={<OwnerFactoryAuthPage />} />
              <Route path="knowledge" element={<KnowledgePage />} />
              <Route path="knowledge/:particleId" element={<KnowledgeDetailPage />} />
              <Route path="knowledge/:particleId/pas" element={<KnowledgePasListPage />} />
              <Route path="knowledge/:particleId/records" element={<LearningRecordPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="profile/password" element={<ChangePasswordPage />} />
              <Route path="profile/api-keys" element={<ApiKeysPage />} />
              <Route path="profile/delete" element={<DeleteAccountPage />} />
              <Route path="profile/models" element={<ProfileModelsPage />} />
              <Route path="review" element={<ReviewHomePage />} />
              <Route path="review/session" element={<ReviewSessionPage />} />
              {TTQ_USER_ENTRY_ENABLED ? (
                <>
                  <Route path="ttq" element={<TtqaListPage />} />
                  <Route path="ttq/recommendations" element={<TtqMembershipPressurePage />} />
                  <Route path="ttq/recommendations/:recommendationId" element={<TtqMembershipPressureDetailPage />} />
                  <Route path="ttq/:ttqaId" element={<TtqSessionPage />} />
                  <Route path="ttq/:ttqaId/ipttq/:ipttqId" element={<TtqIpttqDetailPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/multi-solution/:particleId" element={<TtqMultiSolutionWorkbenchPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/question-chain/:particleId" element={<TtqQuestionChainWorkbenchPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/pattern-unify" element={<TtqPatternUnifyWorkbenchPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/questions-chain" element={<TtqQuestionsChainWorkbenchPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/cttq-converge" element={<TtqCttqWorkbenchPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/aggregate-a" element={<TtqSessionAggregateAWorkbenchPage />} />
                  <Route path="ttq/:ttqaId/ttqworkbench/aggregate-b" element={<TtqSessionAggregateBWorkbenchPage />} />
                </>
              ) : null}
              <Route path="review/sequence" element={<LearningSequencePage />} />
              <Route path="settings/learning" element={<LearningSettingsPage />} />
              <Route path="learning/footprint" element={<LearningFootprintPage />} />
              <Route path="learning/position" element={<LearningPositionPage />} />
              <Route path="learning/spine-coverage" element={<SpineReviewCoveragePage />} />
              <Route path="learning/goals" element={<LearningGoalsPage />} />
              <Route path="learning/subject-modules" element={<SubjectModulesPage />} />
              <Route path="learning/ai-chats" element={<LearningAiChatsPage />} />
              <Route path="learning/ai-chats/:threadId" element={<LearningAiChatDetailPage />} />
              <Route path="bookmarks" element={<BookmarksPage />} />
              <Route path="annotations" element={<AnnotationsPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="scan" element={<ScanUploadPage />} />
              <Route path="about" element={<AboutPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </FactoryWorkspaceProvider>
      </BrowserRouter>
      </DemographicsPromptHost>
    </AuthProvider>
  );
}
